import { hasValue, PushSuccessNotification, styles } from "@repo/helpers";
import { Button, ButtonTypes, Separator, Spinner } from "@repo/ui";
import { Circle, Copy, Dot, FolderOpen, Pencil, Play, Plus, Save, Trash2 } from "lucide-react";
import { useState } from "react";

import type { MapConfiguration } from "../../../models/mapConfiguration";

import {
	useDeleteMapConfiguration,
	useForkConfig,
	useSaveMapConfiguration,
} from "../../../api/mapConfigurationController";
import { useMapLayers } from "../layers/layerContext";
import style from "../layers/style.module.scss";
import { ConfigEditor } from "./configEditor";
import { NEW_MAP_ID, useMapConfig } from "./mapConfigContext";
import configStyle from "./style.module.scss";

export function MapConfigSelector(props: { children?: React.ReactNode; onExecute?: () => void }) {
	const { layers, loadLayers, syncLayerIds, executeAll, hasPendingChanges } = useMapLayers();
	const { mapConfigId, setMapConfigId, configs, currentConfig, startTransition } = useMapConfig();
	const [isEditingConfig, setIsEditingConfig] = useState(false);
	const [configName, setConfigName] = useState("");

	const deleteMutation = useDeleteMapConfiguration();
	const saveMutation = useSaveMapConfiguration();
	const forkConfigMutation = useForkConfig();

	const hasMap = hasValue(mapConfigId);
	const isNewMap = mapConfigId === NEW_MAP_ID;
	const canEditOrDelete = currentConfig && !currentConfig.isReadonly;
	const canExecute = layers.data.some((layer) => layer.itemType);

	const handleLoadConfig = (config: MapConfiguration) => {
		setIsEditingConfig(false);
		startTransition(() => {
			loadLayers(config.layers);
			setMapConfigId(config.id);
			setConfigName(config.name);
		});
	};

	const handleEditConfig = (config: MapConfiguration) => {
		if (mapConfigId !== config.id) handleLoadConfig(config);
		setMapConfigId(config.id);
		setConfigName(config.name);
		setIsEditingConfig(true);
	};

	const handleAddConfig = () => {
		setMapConfigId(NEW_MAP_ID);
		setConfigName("");
		setIsEditingConfig(true);
		loadLayers([]);
	};

	const handleCancelEdit = () => {
		setIsEditingConfig(false);
		if (isNewMap) setMapConfigId(null);
		else setConfigName(currentConfig?.name ?? "");
	};

	const handleSaveConfig = () => {
		if (!configName) return;
		saveMutation.mutate(
			{
				id: canEditOrDelete ? currentConfig.id : NEW_MAP_ID,
				name: configName,
				layers: layers.data.map(({ executedFilters: _executedFilters, ...layer }) => layer),
				isReadonly: false,
			},
			{
				onSuccess: (saved) => {
					syncLayerIds(saved.layers);
					setMapConfigId(saved.id);
					setIsEditingConfig(false);
					PushSuccessNotification("Map saved");
				},
			},
		);
	};

	const handleDeleteConfig = () => {
		if (!confirm("Are you sure you want to delete this configuration?")) return;
		deleteMutation.mutate(mapConfigId, {
			onSuccess: () => {
				setMapConfigId(null);
				setConfigName("");
				setIsEditingConfig(false);
				loadLayers([]);
				PushSuccessNotification("Map deleted");
			},
		});
	};

	const handleForkConfig = () => {
		if (!confirm("Are you sure you want to copy this configuration?")) return;
		forkConfigMutation.mutate(mapConfigId, {
			onSuccess: (forked) => {
				setMapConfigId(forked.id);
				setIsEditingConfig(false);
				PushSuccessNotification("Map copied");
			},
		});
	};

	const renderEditor = (isNew: boolean) => (
		<div key={isNew ? "new" : mapConfigId} className={style.configEditorWrap}>
			<ConfigEditor
				name={configName}
				onNameChange={setConfigName}
				onSave={handleSaveConfig}
				onCancel={handleCancelEdit}
				isNew={isNew}
				isSaving={saveMutation.isPending}
			/>
		</div>
	);

	return (
		<div className={style.section}>
			<section>
				<div className={style.mapsSectionHeader}>
					<h2 className={style.sectionLabel}>Maps</h2>
					{!isEditingConfig && (
						<Button
							types={[ButtonTypes.Primary]}
							onClick={handleAddConfig}
							className={style.newMapButton}
							title="Add configuration"
						>
							<Plus className={configStyle.newMapIcon} />
							New Map
						</Button>
					)}
				</div>

				<div className={style.configList}>
					{!configs.any() && <div className={style.emptyMaps}>No saved maps.</div>}
					{isEditingConfig && isNewMap && renderEditor(true)}
					{configs.map((config) =>
						isEditingConfig && mapConfigId === config.id ? (
							renderEditor(false)
						) : (
							<ConfigRow
								key={config.id}
								config={config}
								isLoaded={mapConfigId === config.id}
								onLoad={() => handleLoadConfig(config)}
								onEdit={() => handleEditConfig(config)}
							/>
						),
					)}
				</div>
			</section>

			{hasMap && props.children}

			<Separator className={configStyle.separator} />

			{hasMap && (
				<div className={style.footer}>
					<Button
						types={[hasPendingChanges ? ButtonTypes.Primary : ButtonTypes.Outline]}
						onClick={() => {
							executeAll();
							props.onExecute?.();
						}}
						disabled={!canExecute}
						fullWidth
						className={style.actionButton}
						title={hasPendingChanges ? "Pending filter changes - click to apply" : undefined}
					>
						<Play className={style.buttonIcon} /> Execute
						{hasPendingChanges && <span className={configStyle.pendingChangesIndicator} />}
					</Button>
					{!isNewMap && (
						<Button
							types={[ButtonTypes.Outline]}
							onClick={handleForkConfig}
							disabled={forkConfigMutation.isPending}
							fullWidth
							className={style.actionButton}
						>
							{forkConfigMutation.isPending ? (
								<Spinner size={16} className={style.buttonIcon} />
							) : (
								<Copy className={style.buttonIcon} />
							)}
							Copy Map
						</Button>
					)}
					{(canEditOrDelete || isNewMap) && (
						<>
							<Button
								types={[ButtonTypes.Primary]}
								onClick={handleSaveConfig}
								disabled={layers.data.length === 0 || saveMutation.isPending}
								fullWidth
								className={style.actionButton}
							>
								{saveMutation.isPending ? (
									<Spinner size={16} className={style.buttonIcon} />
								) : (
									<Save className={style.buttonIcon} />
								)}
								{isNewMap ? "Create Map" : "Save Map"}
							</Button>
							{!isNewMap && (
								<Button
									types={[ButtonTypes.Danger]}
									onClick={handleDeleteConfig}
									disabled={deleteMutation.isPending}
									fullWidth
									className={style.actionButton}
								>
									{deleteMutation.isPending ? (
										<Spinner size={16} className={style.buttonIcon} />
									) : (
										<Trash2 className={style.buttonIcon} />
									)}
									Delete Map
								</Button>
							)}
						</>
					)}
				</div>
			)}
		</div>
	);
}

interface ConfigRowProps {
	config: MapConfiguration;
	isLoaded: boolean;
	onLoad: () => void;
	onEdit: () => void;
}

function ConfigRow({ config, isLoaded, onLoad, onEdit }: ConfigRowProps) {
	return (
		<div className={styles(style.configItem, isLoaded && style.configItemLoaded)}>
			{config.isReadonly ? (
				<div className={configStyle.readonlyConfigIcon}>
					<FolderOpen className={style.configItemIcon} />
				</div>
			) : (
				<Button
					types={[ButtonTypes.Text]}
					size="small"
					className={styles(configStyle.editButtonLayout, style.editConfigButton)}
					onClick={onEdit}
					title="Edit configuration name"
					aria-label={`Edit ${config.name}`}
				>
					<Pencil className={configStyle.editIcon} />
				</Button>
			)}
			<button type="button" onClick={onLoad} className={style.configItemLoad}>
				<span className={style.configItemName}>{config.name}</span>
				<span className={style.configItemRight}>
					{isLoaded ? (
						<span className={style.configItemIndicator}>
							<Circle className={style.configItemIndicatorDot} />
							<Dot className={style.configItemIndicatorDotInner} />
						</span>
					) : (
						<Circle className={style.configItemIndicatorEmpty} />
					)}
				</span>
			</button>
		</div>
	);
}
